import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { UserPlus, Eye, EyeOff, ArrowLeft, Users, Star, User, AlertCircle, QrCode, Smartphone, Copy, CheckCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
// Select supprimé pour stabiliser l'inscription (portail Radix)
// import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Alert, AlertDescription } from '../../components/ui/alert';
import PadaidLogo from '../../components/ui/PadaidLogo';
import { useAuth } from '../../contexts/AuthContext';
import SocialLoginButtons from '../../components/auth/SocialLoginButtons';
import { trackEvent } from '../../services/track';

const Register = () => {
  const { register, setup2FA } = useAuth();
  const [step, setStep] = useState(1); // 1: Registration, 2: 2FA Setup, 3: Success
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    userType: 'membre',
    phone: '',
    motivation: ''
  });
  const [twoFAData, setTwoFAData] = useState({
    qrCode: '',
    secret: '',
    verificationCode: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [secretCopied, setSecretCopied] = useState(false);
  const navigate = useNavigate();

  const userTypes = [
    { value: 'membre', label: 'Membre', description: 'Accès standard aux ressources PADAID', icon: User, color: 'text-primary' },
    { value: 'invite', label: 'Invité', description: 'Accès limité pour découvrir PADAID', icon: Users, color: 'text-secondary' },
    { value: 'co-fondateur', label: 'Co-fondateur', description: 'Accès étendu et privilèges spéciaux', icon: Star, color: 'text-accent' }
    // NOTE: "fondateur" volontairement retiré de l’interface d’inscription
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (error) setError('');
  };

  const handleSelectChange = (value) => {
    setFormData(prev => ({ ...prev, userType: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }
    if (formData.password.length < 8) {
      setError('Le mot de passe doit contenir au moins 8 caractères');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const userData = {
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        password: formData.password,
        phone: formData.phone,
        user_type: formData.userType,
        motivation: formData.motivation
      };
      const result = await register(userData);
      try { trackEvent({ type: 'register', url: window.location.href }); } catch(e){ console.debug('trackEvent register non bloquant', e); }
      setStep(3);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handle2FAVerification = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await setup2FA(twoFAData.verificationCode);
      setStep(3);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const copySecret = async () => {
    try {
      await navigator.clipboard.writeText(twoFAData.secret);
      setSecretCopied(true);
      setTimeout(() => setSecretCopied(false), 2000);
    } catch (err) {
      const textArea = document.createElement('textarea');
      textArea.value = twoFAData.secret;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setSecretCopied(true);
      setTimeout(() => setSecretCopied(false), 2000);
    }
  };

  // const selectedUserType = userTypes.find(type => type.value === formData.userType);

  if (step === 3) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background/90 to-primary/5 flex items-center justify-center p-4">
        <div className="w-full max-w-md text-center">
          <div className="w-16 h-16 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="h-8 w-8 text-green-500" />
          </div>
          <h1 className="text-2xl font-bold mb-4">Inscription réussie !</h1>
          <p className="text-muted-foreground mb-6">Votre compte a été créé avec succès.</p>
          <Button onClick={() => navigate('/login')} className="bg-primary hover:bg-primary/90">Se connecter</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background/90 to-primary/5 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <Link to="/auth" className="inline-flex items-center text-muted-foreground hover:text-primary mb-6">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Retour à l'authentification
          </Link>
          <div className="flex justify-center mb-6"><PadaidLogo size="lg" showText={true} /></div>
          <h2 className="text-3xl font-bold text-foreground">Inscription</h2>
          <p className="mt-2 text-muted-foreground">Rejoignez la communauté PADAID selon votre profil</p>
        </div>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center">
                <UserPlus className="mr-2 h-5 w-5" />
                Créer un compte
              </CardTitle>
            </div>
            <CardDescription className="text-center">Choisissez votre type de compte et remplissez vos informations</CardDescription>
          </CardHeader>
          <CardContent>
            {error && (
              <Alert className="mb-4" variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Type d'utilisateur */}
              <div>
                <Label htmlFor="userType">Type de compte</Label>
                <select
                  id="userType"
                  name="userType"
                  className="mt-1 w-full rounded border bg-background px-3 py-2"
                  value={formData.userType}
                  onChange={(e) => handleSelectChange(e.target.value)}
                >
                  {userTypes.map((type) => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>

              {/* Informations personnelles */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="firstName">Prénom *</Label>
                  <Input id="firstName" name="firstName" type="text" required value={formData.firstName} onChange={handleInputChange} placeholder="Jean" className="mt-1" />
                </div>
                <div>
                  <Label htmlFor="lastName">Nom *</Label>
                  <Input id="lastName" name="lastName" type="text" required value={formData.lastName} onChange={handleInputChange} placeholder="Dupont" className="mt-1" />
                </div>
              </div>

              <div>
                <Label htmlFor="email">Adresse email *</Label>
                <Input id="email" name="email" type="email" autoComplete="email" required value={formData.email} onChange={handleInputChange} placeholder="jean.dupont@email.com" className="mt-1" />
              </div>

              <div>
                <Label htmlFor="phone">Téléphone</Label>
                <Input id="phone" name="phone" type="tel" value={formData.phone} onChange={handleInputChange} placeholder="06 12 34 56 78" className="mt-1" />
              </div>

              {/* Mots de passe */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="password">Mot de passe *</Label>
                  <div className="relative mt-1">
                    <Input id="password" name="password" type={showPassword ? 'text' : 'password'} autoComplete="new-password" required value={formData.password} onChange={handleInputChange} placeholder="••••••••" className="pr-10" />
                    <button type="button" className="absolute inset-y-0 right-0 pr-3 flex items-center" onClick={() => setShowPassword(!showPassword)}>
                      {showPassword ? (<EyeOff className="h-4 w-4 text-muted-foreground" />) : (<Eye className="h-4 w-4 text-muted-foreground" />)}
                    </button>
                  </div>
                </div>
                <div>
                  <Label htmlFor="confirmPassword">Confirmer le mot de passe *</Label>
                  <div className="relative mt-1">
                    <Input id="confirmPassword" name="confirmPassword" type={showConfirmPassword ? 'text' : 'password'} autoComplete="new-password" required value={formData.confirmPassword} onChange={handleInputChange} placeholder="••••••••" className="pr-10" />
                    <button type="button" className="absolute inset-y-0 right-0 pr-3 flex items-center" onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                      {showConfirmPassword ? (<EyeOff className="h-4 w-4 text-muted-foreground" />) : (<Eye className="h-4 w-4 text-muted-foreground" />)}
                    </button>
                  </div>
                </div>
              </div>

              {/* Motivation */}
              {(formData.userType === 'co-fondateur') && (
                <div>
                  <Label htmlFor="motivation">Motivation et expérience *</Label>
                  <Textarea id="motivation" name="motivation" required value={formData.motivation} onChange={handleInputChange} placeholder="Décrivez votre motivation et votre expérience dans le domaine humanitaire..." className="mt-1" rows={4} />
                </div>
              )}

              <Button type="submit" className="w-full bg-primary hover:bg-primary/90" disabled={loading}>{loading ? 'Inscription en cours...' : 'Créer mon compte'}</Button>
            </form>

            <div className="mt-6">
              <div className="relative"><div className="absolute inset-0 flex items-center"><div className="w-full border-t border-border" /></div><div className="relative flex justify-center text-sm"><span className="px-2 bg-card text-muted-foreground">Ou inscrivez-vous avec</span></div></div>
              <div className="mt-6"><SocialLoginButtons /></div>
              <div className="relative mt-6"><div className="absolute inset-0 flex items-center"><div className="w-full border-t border-border" /></div><div className="relative flex justify-center text-sm"><span className="px-2 bg-card text-muted-foreground">Déjà membre ?</span></div></div>
              <div className="mt-6"><Button variant="outline" className="w-full border-primary text-primary hover:bg-primary hover:text-white" asChild><Link to="/login">Se connecter</Link></Button></div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Register;